/**
 * Shoppable Video Reels
 *
 * Behaviour:
 * 1. No video elements are added during initial page rendering.
 * 2. The active video loads only when the section enters the viewport.
 * 3. Other videos load only when their carousel slide becomes selected.
 * 4. All videos pause when the section leaves the viewport.
 * 5. Already loaded videos are reused instead of downloaded again.
 */

/**
 * Video controls custom element
 */
if (!customElements.get('video-controls')) {
  class VideoControls extends HTMLElement {
    constructor() {
      super();

      this.video = null;
      this.soundToggle = null;
      this.playToggle = null;
      this.videoBackground = null;

      this.handleVideoLoaded = this.handleVideoLoaded.bind(this);
      this.handlePlayClick = this.handlePlayClick.bind(this);
      this.handleSoundClick = this.handleSoundClick.bind(this);
      this.updatePlayButton = this.updatePlayButton.bind(this);
      this.updateSoundButton = this.updateSoundButton.bind(this);
    }

    connectedCallback() {
      this.soundToggle = this.querySelector('.video-controls--sound');
      this.playToggle = this.querySelector('.video-controls--play-pause');
      this.videoBackground = this.closest('.slideshow__slide-video-bg');

      if (this.playToggle) {
        this.playToggle.addEventListener('click', this.handlePlayClick);
      }

      if (this.soundToggle) {
        this.soundToggle.addEventListener('click', this.handleSoundClick);
      }

      if (this.videoBackground) {
        this.videoBackground.addEventListener(
          'shoppable:video-loaded',
          this.handleVideoLoaded
        );

        const existingVideo = this.videoBackground.querySelector('video');

        if (existingVideo) {
          this.attachVideo(existingVideo);
        }
      }
    }

    disconnectedCallback() {
      if (this.playToggle) {
        this.playToggle.removeEventListener('click', this.handlePlayClick);
      }

      if (this.soundToggle) {
        this.soundToggle.removeEventListener('click', this.handleSoundClick);
      }

      if (this.videoBackground) {
        this.videoBackground.removeEventListener(
          'shoppable:video-loaded',
          this.handleVideoLoaded
        );
      }

      this.removeVideoListeners();
    }

    handleVideoLoaded(event) {
      if (!event.detail || !event.detail.video) {
        return;
      }

      this.attachVideo(event.detail.video);
    }

    attachVideo(video) {
      if (!video || this.video === video) {
        return;
      }

      this.removeVideoListeners();

      this.video = video;

      this.video.addEventListener('play', this.updatePlayButton);
      this.video.addEventListener('pause', this.updatePlayButton);
      this.video.addEventListener('volumechange', this.updateSoundButton);

      this.updatePlayButton();
      this.updateSoundButton();
    }

    removeVideoListeners() {
      if (!this.video) {
        return;
      }

      this.video.removeEventListener('play', this.updatePlayButton);
      this.video.removeEventListener('pause', this.updatePlayButton);
      this.video.removeEventListener(
        'volumechange',
        this.updateSoundButton
      );
    }

    handlePlayClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.video) {
        return;
      }

      if (this.video.paused) {
        this.video.dataset.userPaused = 'false';

        const playPromise = this.video.play();

        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            // Autoplay may be blocked by the browser.
          });
        }
      } else {
        this.video.dataset.userPaused = 'true';
        this.video.pause();
      }

      this.updatePlayButton();
    }

    handleSoundClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.video) {
        return;
      }

      this.video.muted = !this.video.muted;
      this.updateSoundButton();
    }

    updatePlayButton() {
      if (!this.video || !this.playToggle) {
        return;
      }

      this.playToggle.classList.toggle('paused', this.video.paused);

      this.playToggle.setAttribute(
        'aria-label',
        this.video.paused ? 'Play video' : 'Pause video'
      );
    }

    updateSoundButton() {
      if (!this.video || !this.soundToggle) {
        return;
      }

      this.soundToggle.classList.toggle('muted', this.video.muted);

      this.soundToggle.setAttribute(
        'aria-label',
        this.video.muted ? 'Unmute video' : 'Mute video'
      );
    }
  }

  customElements.define('video-controls', VideoControls);
}

/**
 * Lazy-loading carousel controller
 */
class ShoppableVideoReelsLazyLoader {
  constructor(carousel) {
    this.carousel = carousel;
    this.slides = Array.from(
      this.carousel.querySelectorAll('[data-video-reel-slide]')
    );

    this.isVisible = false;
    this.intersectionObserver = null;
    this.slideObserver = null;

    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleSlideChanges = this.handleSlideChanges.bind(this);

    this.init();
  }

  init() {
    if (!this.carousel || !this.slides.length) {
      return;
    }

    if (this.carousel.dataset.lazyVideoInitialized === 'true') {
      return;
    }

    this.carousel.dataset.lazyVideoInitialized = 'true';

    this.createIntersectionObserver();
    this.createSlideObserver();
  }

  createIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      this.handleIntersection,
      {
        root: null,

        /*
         * No extra loading distance is used.
         * The video loads only after this section actually enters the viewport.
         */
        rootMargin: '0px',

        /*
         * Load when approximately 10% of the carousel is visible.
         */
        threshold: [0, 0.1, 0.25]
      }
    );

    this.intersectionObserver.observe(this.carousel);
  }

  createSlideObserver() {
    this.slideObserver = new MutationObserver(this.handleSlideChanges);

    this.slides.forEach((slide) => {
      this.slideObserver.observe(slide, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden']
      });
    });
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target !== this.carousel) {
        return;
      }

      this.isVisible = entry.isIntersecting && entry.intersectionRatio > 0;

      if (this.isVisible) {
        this.loadSelectedSlide();
        this.syncVideoPlayback();
      } else {
        this.pauseAllVideos();
      }
    });
  }

  handleSlideChanges(mutations) {
    const selectionChanged = mutations.some((mutation) => {
      return (
        mutation.type === 'attributes' &&
        (
          mutation.attributeName === 'class' ||
          mutation.attributeName === 'aria-hidden'
        )
      );
    });

    if (!selectionChanged || !this.isVisible) {
      return;
    }

    /*
     * Wait for the carousel to finish updating its selected classes.
     */
    window.requestAnimationFrame(() => {
      this.loadSelectedSlide();
      this.syncVideoPlayback();
    });
  }

  getSelectedSlide() {
    /*
     * First try the normal Flickity selected class.
     */
    let selectedSlide = this.carousel.querySelector(
      '[data-video-reel-slide].is-selected'
    );

    /*
     * Some carousel implementations use aria-hidden instead.
     */
    if (!selectedSlide) {
      selectedSlide = this.carousel.querySelector(
        '[data-video-reel-slide][aria-hidden="false"]'
      );
    }

    /*
     * Before the carousel JavaScript finishes initializing,
     * use the first slide as the active slide.
     */
    if (!selectedSlide) {
      selectedSlide = this.slides[0];
    }

    return selectedSlide;
  }

  isSlideSelected(slide) {
    if (!slide) {
      return false;
    }

    if (slide.classList.contains('is-selected')) {
      return true;
    }

    if (slide.getAttribute('aria-hidden') === 'false') {
      return true;
    }

    /*
     * Use the first slide while the carousel is still initializing.
     */
    const hasAnySelectedSlide = this.carousel.querySelector(
      '[data-video-reel-slide].is-selected, ' +
      '[data-video-reel-slide][aria-hidden="false"]'
    );

    return !hasAnySelectedSlide && slide === this.slides[0];
  }

  loadSelectedSlide() {
    if (!this.isVisible) {
      return;
    }

    const selectedSlide = this.getSelectedSlide();

    if (!selectedSlide) {
      return;
    }

    this.loadVideoForSlide(selectedSlide);
  }

  loadVideoForSlide(slide) {
    const lazyContainer = slide.querySelector(
      '[data-lazy-video-container]'
    );

    if (!lazyContainer) {
      return null;
    }

    /*
     * The video has already been inserted.
     */
    if (lazyContainer.dataset.videoLoaded === 'true') {
      return lazyContainer.querySelector('video');
    }

    const videoTemplate = lazyContainer.querySelector(
      '[data-lazy-video-template]'
    );

    if (!videoTemplate) {
      return null;
    }

    /*
     * Clone the video from the inert template.
     * The browser starts requesting the video only at this point.
     */
    const videoFragment = videoTemplate.content.cloneNode(true);
    const video = videoFragment.querySelector('video');

    if (!video) {
      return null;
    }

    video.autoplay = false;
    video.loop = true;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.dataset.userPaused = 'false';

    lazyContainer.appendChild(videoFragment);
    lazyContainer.dataset.videoLoaded = 'true';

    const insertedVideo = lazyContainer.querySelector('video');

    if (!insertedVideo) {
      return null;
    }

    insertedVideo.addEventListener(
      'loadeddata',
      () => {
        lazyContainer.dataset.videoReady = 'true';
      },
      {
        once: true
      }
    );

    insertedVideo.addEventListener(
      'canplay',
      () => {
        lazyContainer.dataset.videoReady = 'true';

        if (
          this.isVisible &&
          this.isSlideSelected(slide) &&
          insertedVideo.dataset.userPaused !== 'true'
        ) {
          this.safePlay(insertedVideo);
        }
      },
      {
        once: true
      }
    );

    /*
     * Inform the controls that their video now exists.
     */
    const videoBackground = lazyContainer.closest(
      '.slideshow__slide-video-bg'
    );

    if (videoBackground) {
      videoBackground.dispatchEvent(
        new CustomEvent('shoppable:video-loaded', {
          bubbles: true,
          detail: {
            video: insertedVideo
          }
        })
      );
    }

    /*
     * Explicitly begin loading only after the slide is needed.
     */
    insertedVideo.load();

    if (
      this.isVisible &&
      this.isSlideSelected(slide) &&
      insertedVideo.dataset.userPaused !== 'true'
    ) {
      this.safePlay(insertedVideo);
    }

    return insertedVideo;
  }

  syncVideoPlayback() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');

      if (!video) {
        return;
      }

      const shouldPlay =
        this.isVisible &&
        this.isSlideSelected(slide) &&
        video.dataset.userPaused !== 'true';

      if (shouldPlay) {
        this.safePlay(video);
      } else {
        video.pause();
      }
    });
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');

      if (video && !video.paused) {
        video.pause();
      }
    });
  }

  safePlay(video) {
    if (!video || video.dataset.userPaused === 'true') {
      return;
    }

    const playPromise = video.play();

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        /*
         * Some mobile browsers may reject autoplay.
         * The play button remains available for manual playback.
         */
      });
    }
  }
}

/**
 * Initialize every shoppable video reels section.
 */
function initializeShoppableVideoReels(container) {
  const scope = container || document;

  const carousels = scope.querySelectorAll(
    '[data-shoppable-video-reels]'
  );

  carousels.forEach((carousel) => {
    if (carousel.dataset.lazyVideoInitialized === 'true') {
      return;
    }

    new ShoppableVideoReelsLazyLoader(carousel);
  });
}

/**
 * Initialize only after the DOM is ready.
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    initializeShoppableVideoReels(document);
  });
} else {
  initializeShoppableVideoReels(document);
}

/**
 * Reinitialize sections added through the Shopify Theme Editor.
 */
document.addEventListener('shopify:section:load', function (event) {
  initializeShoppableVideoReels(event.target);
});